import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Cog, ExternalLink, FileText, Loader2, Paperclip, Pencil, Plus, Trash2, Wrench, X } from "lucide-react";

const DOC_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg";
const STATUS_META = {
  requested: { label: "Requested", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ordered: { label: "Ordered", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  received: { label: "Received", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-600 border-red-200" },
};
const KIND_META = {
  issue: { label: "Issue", cls: "bg-red-50 text-red-600 border-red-200" },
  performance: { label: "Performance", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const err = (e, fb) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fb);

function MachineDialog({ open, onClose, machine, onSaved }) {
  const [form, setForm] = useState({ name: "", manufacturer: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: machine?.name || "", manufacturer: machine?.manufacturer || "", notes: machine?.notes || "" });
  }, [open, machine]);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Machine name is required"); return; }
    setBusy(true);
    try {
      if (machine) await api.put(`/machines/${machine.id}`, form);
      else await api.post("/machines", form);
      toast.success(machine ? "Machine updated" : `${form.name} added`);
      onSaved(); onClose();
    } catch (e) { err(e, "Save failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md">
        <DialogHeader><DialogTitle>{machine ? "Edit Machine" : "Add Machine"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Machine name *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sidel Blower" className="rounded-sm" data-testid="machine-name-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Manufacturer / model</label>
            <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} placeholder="e.g. Sidel, AROL, Krones, OCME" className="rounded-sm" data-testid="machine-manufacturer-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm" data-testid="machine-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#002FA7] hover:bg-[#00227a]" data-testid="machine-save-btn">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDialog({ open, onClose, machine, order, onSaved }) {
  const empty = { item_name: "", purpose: "", order_type: "spare", checkout_url: "", status: "requested" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(order ? { item_name: order.item_name, purpose: order.purpose || "", order_type: order.order_type, checkout_url: order.checkout_url || "", status: order.status } : empty);
  }, [open, order]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    if (!form.item_name.trim()) { toast.error("Item name is required"); return; }
    setBusy(true);
    try {
      if (order) await api.put(`/machines/${machine.id}/orders/${order.id}`, form);
      else await api.post(`/machines/${machine.id}/orders`, form);
      toast.success(order ? "Order updated" : "Order request added");
      onSaved(); onClose();
    } catch (e) { err(e, "Save failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md">
        <DialogHeader><DialogTitle>{order ? "Edit Order Request" : `New Order Request — ${machine?.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Item name *</label>
            <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="e.g. Gripper belt" className="rounded-sm" data-testid="order-item-input" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Purpose of item</label>
            <Textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} rows={2} placeholder="What is it for / why is it needed" className="rounded-sm" data-testid="order-purpose-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Type</label>
              <Select value={form.order_type} onValueChange={(v) => setForm({ ...form, order_type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="order-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="replacement">Replacement</SelectItem>
                  <SelectItem value="spare">Spare part</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Checkout link</label>
            <Input value={form.checkout_url} onChange={(e) => setForm({ ...form, checkout_url: e.target.value })} placeholder="https://…" className="rounded-sm" data-testid="order-checkout-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#002FA7] hover:bg-[#00227a]" data-testid="order-save-btn">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocCell({ machine, order, kind, onChanged }) {
  const [busy, setBusy] = useState(false);
  const docs = order[`${kind}_docs`] || [];
  const upload = async (f) => {
    setBusy(true);
    try {
      const d = await uploadDocument(f);
      await api.post(`/machines/${machine.id}/orders/${order.id}/docs`, { kind, name: d.name || f.name, url: d.url, path: d.path });
      toast.success(`${kind === "quote" ? "Quotation" : "Invoice"} attached`);
      onChanged();
    } catch (e) { err(e, "Upload failed"); } finally { setBusy(false); }
  };
  const remove = async (i) => {
    try { await api.delete(`/machines/${machine.id}/orders/${order.id}/docs/${kind}/${i}`); onChanged(); }
    catch (e) { err(e, "Failed to remove"); }
  };
  return (
    <div className="flex flex-col gap-0.5">
      {docs.map((d, i) => (
        <span key={`${d.path}-${i}`} className="inline-flex max-w-[160px] items-center gap-1 text-xs">
          <a href={d.url} target="_blank" rel="noreferrer" className="truncate text-[#002FA7] hover:underline" data-testid={`order-${kind}-file-${order.id}-${i}`}>{d.name}</a>
          <button onClick={() => remove(i)} className="shrink-0 text-slate-400 hover:text-red-600" data-testid={`order-${kind}-remove-${order.id}-${i}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
      <label className="inline-flex w-fit cursor-pointer items-center gap-1 text-xs font-semibold text-slate-400 hover:text-[#002FA7]" data-testid={`order-${kind}-upload-${order.id}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
        {docs.length ? "Add" : "Attach"}
        <input type="file" className="hidden" accept={DOC_ACCEPT} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
      </label>
    </div>
  );
}

function OrdersTab({ machine, onCounts }) {
  const [orders, setOrders] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try { setOrders((await api.get(`/machines/${machine.id}/orders`)).data); onCounts(); }
    catch (e) { err(e, "Failed to load orders"); }
  }, [machine.id, onCounts]);
  useEffect(() => { setOrders(null); load(); }, [load]);
  const setStatus = async (o, status) => {
    try {
      await api.put(`/machines/${machine.id}/orders/${o.id}`, { item_name: o.item_name, purpose: o.purpose, order_type: o.order_type, checkout_url: o.checkout_url, status });
      load();
    } catch (e) { err(e, "Failed to update status"); }
  };
  const del = async (o) => {
    if (!window.confirm(`Delete order request "${o.item_name}"?`)) return;
    try { await api.delete(`/machines/${machine.id}/orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-slate-500">{orders ? `${orders.length} order request${orders.length === 1 ? "" : "s"}` : ""}</p>
        <Button size="sm" onClick={() => { setEditing(null); setDlg(true); }} className="rounded-sm bg-[#002FA7] hover:bg-[#00227a]" data-testid="add-order-btn">
          <Plus className="mr-1 h-3.5 w-3.5" />Order Request
        </Button>
      </div>
      {!orders ? <p className="py-8 text-center text-sm text-slate-400">Loading…</p>
        : !orders.length ? <p className="rounded-sm border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">No order requests yet</p>
        : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                <th className="px-2 py-2">Item</th><th className="px-2 py-2">Purpose</th><th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Status</th><th className="px-2 py-2">Checkout</th><th className="px-2 py-2">Quotation</th>
                <th className="px-2 py-2">Invoice</th><th className="px-2 py-2">Date</th><th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-100 align-top hover:bg-slate-50/60" data-testid={`order-row-${o.id}`}>
                  <td className="px-2 py-2 font-semibold text-slate-800">{o.item_name}</td>
                  <td className="max-w-[220px] whitespace-pre-wrap px-2 py-2 text-xs text-slate-500">{o.purpose || "—"}</td>
                  <td className="px-2 py-2 text-xs capitalize text-slate-600">{o.order_type === "spare" ? "Spare part" : "Replacement"}</td>
                  <td className="px-2 py-2">
                    <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
                      <SelectTrigger className={`h-6 w-[110px] rounded-sm border px-2 text-[11px] font-semibold ${STATUS_META[o.status]?.cls || ""}`} data-testid={`order-status-${o.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-2">
                    {o.checkout_url ? (
                      <a href={o.checkout_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline" data-testid={`order-checkout-${o.id}`}>
                        Link <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="px-2 py-2"><DocCell machine={machine} order={o} kind="quote" onChanged={load} /></td>
                  <td className="px-2 py-2"><DocCell machine={machine} order={o} kind="invoice" onChanged={load} /></td>
                  <td className="px-2 py-2 text-xs tabular-nums text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => { setEditing(o); setDlg(true); }} className="text-slate-400 hover:text-[#002FA7]" title="Edit" data-testid={`order-edit-${o.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(o)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`order-delete-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <OrderDialog open={dlg} onClose={() => setDlg(false)} machine={machine} order={editing} onSaved={load} />
    </div>
  );
}

function ReportDialog({ open, onClose, machine, report, onSaved }) {
  const [form, setForm] = useState({ report_date: "", kind: "issue", text: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(report ? { report_date: report.report_date, kind: report.kind, text: report.text }
      : { report_date: new Date().toISOString().slice(0, 10), kind: "issue", text: "" });
  }, [open, report]);
  const save = async () => {
    if (!form.text.trim()) { toast.error("Report text is required"); return; }
    setBusy(true);
    try {
      if (report) await api.put(`/machines/${machine.id}/reports/${report.id}`, form);
      else await api.post(`/machines/${machine.id}/reports`, form);
      toast.success(report ? "Report updated" : "Report added");
      onSaved(); onClose();
    } catch (e) { err(e, "Save failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md">
        <DialogHeader><DialogTitle>{report ? "Edit Report" : `New Maintenance Report — ${machine?.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Date</label>
              <Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} className="rounded-sm" data-testid="report-date-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Type</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="rounded-sm" data-testid="report-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="issue">Issue</SelectItem>
                  <SelectItem value="performance">Performance note</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Report *</label>
            <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={5} placeholder="Describe the issue or the machine's performance" className="rounded-sm" data-testid="report-text-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#002FA7] hover:bg-[#00227a]" data-testid="report-save-btn">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportsTab({ machine, onCounts }) {
  const [reports, setReports] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try { setReports((await api.get(`/machines/${machine.id}/reports`)).data); onCounts(); }
    catch (e) { err(e, "Failed to load reports"); }
  }, [machine.id, onCounts]);
  useEffect(() => { setReports(null); load(); }, [load]);
  const del = async (r) => {
    if (!window.confirm("Delete this report?")) return;
    try { await api.delete(`/machines/${machine.id}/reports/${r.id}`); toast.success("Report deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-slate-500">{reports ? `${reports.length} report${reports.length === 1 ? "" : "s"}` : ""}</p>
        <Button size="sm" onClick={() => { setEditing(null); setDlg(true); }} className="rounded-sm bg-[#002FA7] hover:bg-[#00227a]" data-testid="add-report-btn">
          <Plus className="mr-1 h-3.5 w-3.5" />Maintenance Report
        </Button>
      </div>
      {!reports ? <p className="py-8 text-center text-sm text-slate-400">Loading…</p>
        : !reports.length ? <p className="rounded-sm border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">No maintenance reports yet</p>
        : (
        <div className="space-y-2">
          {reports.map((r) => (
            <div key={r.id} className="rounded-sm border border-slate-200 p-3" data-testid={`report-row-${r.id}`}>
              <div className="mb-1 flex items-center gap-2">
                <span className={`inline-flex rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${KIND_META[r.kind]?.cls || ""}`}>{KIND_META[r.kind]?.label || r.kind}</span>
                <span className="text-xs tabular-nums text-slate-500">{r.report_date}</span>
                <span className="ml-auto flex items-center gap-1.5">
                  <button onClick={() => { setEditing(r); setDlg(true); }} className="text-slate-400 hover:text-[#002FA7]" title="Edit" data-testid={`report-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`report-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-slate-700">{r.text}</p>
            </div>
          ))}
        </div>
      )}
      <ReportDialog open={dlg} onClose={() => setDlg(false)} machine={machine} report={editing} onSaved={load} />
    </div>
  );
}

export default function Machines() {
  const [machines, setMachines] = useState(null);
  const [selId, setSelId] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/machines");
      setMachines(data);
      setSelId((cur) => (data.some((m) => m.id === cur) ? cur : data[0]?.id || ""));
    } catch (e) { err(e, "Failed to load machines"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const [dragId, setDragId] = useState(null);
  const sel = (machines || []).find((m) => m.id === selId);
  const onDropOn = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const list = [...machines];
    const from = list.findIndex((m) => m.id === dragId);
    const to = list.findIndex((m) => m.id === targetId);
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setMachines(list);
    setDragId(null);
    try { await api.post("/machines/reorder", { ids: list.map((m) => m.id) }); }
    catch (e) { err(e, "Failed to save order"); load(); }
  };
  const del = async (m) => {
    if (!window.confirm(`Delete machine "${m.name}"?`)) return;
    try { await api.delete(`/machines/${m.id}`); toast.success("Machine deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };
  return (
    <div className="p-6 md:p-8" data-testid="machines-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Operations</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Machine Maintenance</h1>
        </div>
        <Button onClick={() => { setEditing(null); setDlg(true); }} className="rounded-sm bg-[#002FA7] hover:bg-[#00227a]" data-testid="add-machine-btn">
          <Plus className="mr-1.5 h-4 w-4" />Add Machine
        </Button>
      </div>

      {!machines ? <p className="py-16 text-center text-sm text-slate-400">Loading…</p>
        : !machines.length ? (
        <div className="rounded-sm border border-dashed border-slate-200 py-16 text-center">
          <Cog className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">No machines yet — add your first machine (Sidel, AROL, Krones, OCME…)</p>
        </div>
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="w-full shrink-0 lg:w-64">
            <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="machines-status-summary">
              <span className="inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid="summary-requested-tag">
                Requests {machines.reduce((s, m) => s + (m.requested_count || 0), 0)}
              </span>
              <span className="inline-flex items-center gap-1 rounded-sm border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700" data-testid="summary-ordered-tag">
                Ordered {machines.reduce((s, m) => s + (m.ordered_count || 0), 0)}
              </span>
              <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700" data-testid="summary-received-tag">
                Received {machines.reduce((s, m) => s + (m.received_count || 0), 0)}
              </span>
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="machines-report-summary">
              <span className="text-[11px] font-semibold text-slate-500">Reports:</span>
              <span className="inline-flex items-center gap-1 rounded-sm border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-600" data-testid="summary-issue-tag">
                Issues {machines.reduce((s, m) => s + (m.issue_count || 0), 0)}
              </span>
              <span className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600" data-testid="summary-performance-tag">
                Performance {machines.reduce((s, m) => s + (m.performance_count || 0), 0)}
              </span>
            </div>
            <div className="space-y-1.5">
            {machines.map((m) => (
              <button key={m.id} onClick={() => setSelId(m.id)}
                draggable
                onDragStart={(e) => { setDragId(m.id); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={(e) => { e.preventDefault(); onDropOn(m.id); }}
                onDragEnd={() => setDragId(null)}
                className={`group w-full cursor-grab rounded-sm border px-3 py-2.5 text-left transition-colors active:cursor-grabbing ${m.id === selId ? "border-[#002FA7] bg-blue-50/50" : "border-slate-200 hover:border-slate-300"} ${dragId === m.id ? "opacity-40" : ""}`}
                data-testid={`machine-card-${m.id}`}>
                <span className="flex items-center gap-2">
                  <Cog className={`h-4 w-4 shrink-0 ${m.id === selId ? "text-[#002FA7]" : "text-slate-400"}`} />
                  <span className="truncate text-sm font-semibold text-slate-800">{m.name}</span>
                  <span className="ml-auto flex shrink-0 items-center gap-1">
                    <span className="rounded-sm border border-amber-200 bg-amber-50 px-1.5 text-[10px] font-bold leading-4 text-amber-700" title="Requested" data-testid={`machine-requested-tag-${m.id}`}>{m.requested_count || 0}</span>
                    <span className="rounded-sm border border-blue-200 bg-blue-50 px-1.5 text-[10px] font-bold leading-4 text-blue-700" title="Ordered" data-testid={`machine-ordered-tag-${m.id}`}>{m.ordered_count || 0}</span>
                    <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-1.5 text-[10px] font-bold leading-4 text-emerald-700" title="Received" data-testid={`machine-received-tag-${m.id}`}>{m.received_count || 0}</span>
                  </span>
                </span>
                <span className="mt-1 flex items-center justify-end gap-1">
                  <span className="text-[10px] font-semibold text-slate-400">Reports:</span>
                  <span className="rounded-sm border border-red-200 bg-red-50 px-1.5 text-[10px] font-bold leading-4 text-red-600" title="Issue reports" data-testid={`machine-issue-tag-${m.id}`}>{m.issue_count || 0}</span>
                  <span className="rounded-sm border border-slate-200 bg-slate-100 px-1.5 text-[10px] font-bold leading-4 text-slate-600" title="Performance reports" data-testid={`machine-performance-tag-${m.id}`}>{m.performance_count || 0}</span>
                </span>
                {m.manufacturer && <span className="mt-0.5 block truncate pl-6 text-xs text-slate-400">{m.manufacturer}</span>}
                <span className="mt-0.5 block pl-6 text-[11px] text-slate-400">{m.order_count} order{m.order_count === 1 ? "" : "s"} · {m.report_count} report{m.report_count === 1 ? "" : "s"}</span>
              </button>
            ))}
            </div>
          </div>

          {sel && (
            <div className="min-w-0 flex-1 rounded-sm border border-slate-200 p-4 md:p-5" data-testid="machine-detail">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{sel.name}</h2>
                  {sel.manufacturer && <p className="text-xs text-slate-500">{sel.manufacturer}</p>}
                  {sel.notes && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-400">{sel.notes}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => { setEditing(sel); setDlg(true); }} className="rounded-sm border border-slate-200 p-1.5 text-slate-400 hover:text-[#002FA7]" title="Edit machine" data-testid={`machine-edit-${sel.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(sel)} className="rounded-sm border border-slate-200 p-1.5 text-slate-400 hover:text-red-600" title="Delete machine" data-testid={`machine-delete-${sel.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <Tabs defaultValue="orders">
                <TabsList className="rounded-sm">
                  <TabsTrigger value="orders" className="rounded-sm text-xs" data-testid="tab-orders"><FileText className="mr-1.5 h-3.5 w-3.5" />Order Requests</TabsTrigger>
                  <TabsTrigger value="reports" className="rounded-sm text-xs" data-testid="tab-reports"><Wrench className="mr-1.5 h-3.5 w-3.5" />Maintenance Reports</TabsTrigger>
                </TabsList>
                <TabsContent value="orders" className="mt-4"><OrdersTab machine={sel} onCounts={load} /></TabsContent>
                <TabsContent value="reports" className="mt-4"><ReportsTab machine={sel} onCounts={load} /></TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      )}
      <MachineDialog open={dlg} onClose={() => setDlg(false)} machine={editing} onSaved={load} />
    </div>
  );
}
